import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Keyword, LandscapeSourceTag, SimpleSourceTag, SourceTag, UtilizationTag } from "@/types";
import { getVisibleUtilizationTags } from "./utils";

const MAX_VISIBLE_TAGS = 2;

function getUtilizationTagLabel(tag: UtilizationTag): string {
  switch (tag) {
    case UtilizationTag.TRACKED: return "Tracked";
    case UtilizationTag.LANDSCAPED: return "Landscaped";
    case UtilizationTag.A_B_TESTING: return "A/B Testing";
    default: return tag;
  }
}

export function getUtilizationTagColor(tag: UtilizationTag) {
  switch (tag) {
    case UtilizationTag.LANDSCAPED: return "bg-[hsl(185,70%,45%)]/20 text-foreground border-[hsl(185,70%,45%)]/30 font-semibold";
    case UtilizationTag.A_B_TESTING: return "bg-[hsl(35,90%,50%)]/20 text-foreground border-[hsl(35,90%,50%)]/30 font-semibold";
    case UtilizationTag.TRACKED: return "bg-primary/20 text-foreground border-primary/30 font-semibold";
    default: return "bg-muted text-foreground border-muted font-semibold";
  }
}

function renderSourceTag(tag: SourceTag, i: number, onOpenLandscape?: (landscapeId: number) => void) {
  if (typeof tag === "string") {
    // AUT-419: "Signal AI" is no longer surfaced in the Source column (filtered out in SourceCell);
    // guard here too so it never renders if reached directly.
    if (tag === SimpleSourceTag.SIGNAL_AI) {
      return null;
    }
    if (tag === SimpleSourceTag.SQPR) {
      return (
        <Badge key={i} variant="outline" className="text-[10px] font-semibold px-1.5 py-0 h-5 whitespace-nowrap border-[hsl(0,80%,65%)] text-[hsl(0,80%,55%)] bg-[hsl(0,80%,65%)]/10">
          SQPR
        </Badge>
      );
    }
    if (tag === SimpleSourceTag.LANDSCAPE) {
      return (
        <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-foreground bg-[hsl(var(--trend-positive))]/10 border border-dashed border-[hsl(var(--trend-positive))]/40 rounded-full px-1.5 py-0 h-5 whitespace-nowrap">
          Landscape
          <ArrowUpRight className="h-2.5 w-2.5 shrink-0" />
        </span>
      );
    }
    if (tag === SimpleSourceTag.SUBCATEGORY_DISCOVERY) {
      return (
        // AUT-419: match the prototype's purple Sub-Category badge.
        <Badge key={i} className="text-[10px] font-semibold px-1.5 py-0 h-5 whitespace-nowrap bg-[hsl(275_65%_90%)] text-foreground border-0">
          Sub-Category
        </Badge>
      );
    }
    return (
      <Badge key={i} variant="outline" className="text-[10px] font-semibold px-1.5 py-0 h-5 whitespace-nowrap bg-muted/50 text-muted-foreground border-border">
        Manual
      </Badge>
    );
  }
  const lt = tag as LandscapeSourceTag;
  return (
    <TooltipProvider key={i} delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-foreground bg-[hsl(185,70%,45%)]/10 border border-[hsl(185,70%,45%)]/30 rounded-full px-1.5 py-0 h-5 cursor-pointer whitespace-nowrap hover:bg-[hsl(185,70%,45%)]/20 transition-colors leading-tight"
            onClick={() => lt.landscapeId && onOpenLandscape?.(lt.landscapeId)}
          >
            <span className="border-b border-dotted border-foreground/40">Landscape</span>
            <ArrowUpRight className="h-2.5 w-2.5 shrink-0" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="font-medium">{lt.landscapeName}</p>
          <p className="text-xs text-muted-foreground">Click to open landscape</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getSourceTagLabel(tag: SourceTag): string {
  if (typeof tag === "string") return tag;
  return (tag as LandscapeSourceTag).landscapeName;
}

function OverflowBubble<T>({ tags, renderTag }: { tags: T[]; renderTag: (tag: T, i: number) => React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center justify-center text-[10px] font-semibold text-muted-foreground bg-muted border border-border rounded-full px-1.5 h-5 cursor-pointer whitespace-nowrap hover:bg-muted/80 transition-colors">
          +{tags.length}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="p-2 w-auto">
        <div className="flex flex-wrap gap-1">
          {tags.map((tag, i) => renderTag(tag, i))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function UtilizationCell({
  keyword,
  align = "center",
}: {
  keyword: Keyword;
  /** AUT-455: the product Keywords tab centers this cell (its header is `text-center`); the A/B
   *  creation bank picker's header is left-aligned, so it passes `align="start"`. Defaults to the
   *  existing centered behaviour so `KeywordMetricCells.tsx` (the product tab) is unaffected. */
  align?: "center" | "start";
}) {
  const tags = getVisibleUtilizationTags(keyword);
  if (tags.length === 0) return <span className="text-muted-foreground text-xs">—</span>;

  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = tags.slice(MAX_VISIBLE_TAGS);

  return (
    <div className={cn("flex flex-nowrap items-center gap-1 w-full", align === "start" ? "justify-start" : "justify-center")}>
      {visible.map(tag => (
        <Badge key={tag} variant="outline" className={cn("text-[10px] px-1.5 py-0 h-5 whitespace-nowrap", getUtilizationTagColor(tag))}>
          {getUtilizationTagLabel(tag)}
        </Badge>
      ))}
      {overflow.length > 0 && (
        <OverflowBubble
          tags={overflow}
          renderTag={(tag: UtilizationTag, i: number) => (
            <Badge key={i} variant="outline" className={cn("text-[10px] px-1.5 py-0 h-5 whitespace-nowrap", getUtilizationTagColor(tag))}>
              {getUtilizationTagLabel(tag)}
            </Badge>
          )}
        />
      )}
    </div>
  );
}

export function SourceCell({
  keyword,
  onOpenLandscape,
  align = "center",
}: {
  keyword: Keyword;
  onOpenLandscape?: (landscapeId: number) => void;
  /** AUT-455: see `UtilizationCell`'s `align` doc. */
  align?: "center" | "start";
}) {
  // AUT-419: drop "Signal AI" so it never appears in the Source column (and never counts toward the
  // visible/overflow split). Landscape (object) tags are always kept.
  const tags = (keyword.sourceTags ?? []).filter((t) => typeof t !== "string" || t !== SimpleSourceTag.SIGNAL_AI);
  if (tags.length === 0) return <span className="text-muted-foreground text-xs">—</span>;

  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = tags.slice(MAX_VISIBLE_TAGS);

  return (
    <div className={cn("flex flex-nowrap items-center gap-1 w-full", align === "start" ? "justify-start" : "justify-center")}>
      {visible.map((tag, i) => renderSourceTag(tag, i, onOpenLandscape))}
      {overflow.length > 0 && (
        <OverflowBubble
          tags={overflow}
          renderTag={(tag: SourceTag, i: number) => renderSourceTag(tag, i, onOpenLandscape)}
        />
      )}
    </div>
  );
}
